import { useId } from "react";
import PieceLockShineSvg from "./PieceLockShineOverlay";
import { PUZZLE_3D_BEVEL_FILTER_BASE, PUZZLE_3D_DIE_STROKE, Puzzle3DBevelFilter } from "./Puzzle3DBevelFilter";

export const BOARD_SIZE = 500;
export const SVG_SIZE = 1536;

/** SVG viewBox padding around each cell — tabs extend beyond the cell square. */
export const PIECE_VISUAL_MARGIN_RATIO = 0.35;

/** Full on-screen size of a piece including tabs (px), given board cell size. */
export function pieceVisualRenderSizePx(cellPx) {
    return cellPx * (1 + 2 * PIECE_VISUAL_MARGIN_RATIO);
}

/** Tab overhang on each side of the cell square (px). */
export function pieceVisualCellOffsetPx(cellPx) {
    return cellPx * PIECE_VISUAL_MARGIN_RATIO;
}

const gridCoordBadgeBase = {
    position: "absolute",
    top: 1,
    left: 1,
    padding: "0 1px",
    lineHeight: 1,
    fontFamily: "ui-monospace, monospace",
    fontWeight: 600,
    color: "#fff",
    background: "rgba(0,0,0,0.72)",
    borderRadius: 1,
    pointerEvents: "none",
    zIndex: 2,
};

function gridCoordBadgeStyle(cellPx) {
    return {
        ...gridCoordBadgeBase,
        fontSize: Math.max(5, Math.min(7, Math.round(cellPx * 0.085))),
    };
}

/** Each SVG instance needs unique clipPath ids (drag ghost + board + tray would otherwise share ids and break clipping). */
export function PieceVisual({
    pieceIndex,
    imageUrl,
    grid,
    paths,
    showGridCoords = false,
    bevel3d = true,
    bevelStrokeWidth = 1,
    /** When true (board/scatter coords), align the cell origin; when false (centered tray/list), use full bounds. */
    anchorToCell = true,
    /** When set, plays a one-shot lock shine overlay aligned to this piece. */
    lockShineKey = null,
}) {
    const uid = useId().replace(/[^a-zA-Z0-9_-]/g, "");
    const filterId = `${PUZZLE_3D_BEVEL_FILTER_BASE}-${uid}`;
    const cellSvg = SVG_SIZE / grid;
    const cellPx = BOARD_SIZE / grid;
    const col = pieceIndex % grid;
    const row = Math.floor(pieceIndex / grid);
    const pieceData = paths[pieceIndex];
    if (!pieceData?.path) return null;
    const clipId = `jcp${uid}g${grid}p${pieceIndex}`;
    const margin = cellSvg * PIECE_VISUAL_MARGIN_RATIO;
    const vbSpan = cellSvg + margin * 2;
    const vbX = col * cellSvg - margin;
    const vbY = row * cellSvg - margin;
    const renderSize = (vbSpan / cellSvg) * cellPx;
    const offset = pieceVisualCellOffsetPx(cellPx);
    const viewBox = `${vbX} ${vbY} ${vbSpan} ${vbSpan}`;

    return (
        <div
            style={{
                position: "relative",
                width: renderSize,
                height: renderSize,
                ...(anchorToCell ? { marginLeft: -offset, marginTop: -offset } : null),
                flexShrink: 0,
            }}
        >
            <svg
                width={renderSize}
                height={renderSize}
                viewBox={viewBox}
                style={{ display: "block", overflow: "visible" }}
                overflow="visible"
            >
                <defs>
                    <clipPath id={clipId}>
                        <path d={pieceData.path} />
                    </clipPath>
                    {bevel3d ? <Puzzle3DBevelFilter filterId={filterId} /> : null}
                </defs>
                <image
                    href={imageUrl}
                    x="0"
                    y="0"
                    width={SVG_SIZE}
                    height={SVG_SIZE}
                    preserveAspectRatio="xMinYMin slice"
                    clipPath={`url(#${clipId})`}
                />
                {bevel3d ? (
                    <path
                        d={pieceData.path}
                        fill="none"
                        stroke={PUZZLE_3D_DIE_STROKE}
                        strokeWidth={bevelStrokeWidth}
                        strokeLinejoin="round"
                        strokeLinecap="round"
                        vectorEffect="nonScalingStroke"
                        filter={`url(#${filterId})`}
                    />
                ) : null}
            </svg>
            {lockShineKey ? (
                <PieceLockShineSvg
                    key={lockShineKey}
                    path={pieceData.path}
                    viewBox={viewBox}
                    renderSize={renderSize}
                    viewBoxX={vbX}
                    viewBoxY={vbY}
                    viewBoxSize={vbSpan}
                />
            ) : null}
            {showGridCoords && grid > 0 && (
                <span style={{ ...gridCoordBadgeStyle(cellPx), top: offset + 1, left: offset + 1 }}>
                    ({col},{row})
                </span>
            )}
        </div>
    );
}
